import * as R from 'ramda'

import {
  AssessmentEquipmentAnswers,
  AssessmentEquipmentGroupAnswers,
  AssessmentQuestion,
  AssessmentQuestionKey,
  BuildingQuestionKey,
  ClientFrontlineConfig,
  EquipmentCategory,
  EquipmentGroupStId,
  EquipmentQuestionKey,
  EquipmentStId,
  FlexibleScope,
  Offer,
  OfferingMatrixData,
  ElevatorAssessmentQuestion,
  ElevatorQuestionKey,
  EscalatorQuestionKey,
  EscalatorAssessmentQuestion,
  DoorAssessmentQuestion,
  DoorQuestionKey
} from '../../../types/types'
import { CriticalityOrCostSensitivity, preferredOfferCalculation } from './preferred-offering-calculation'
import { translateXLAssessmentQuestionAnswerKey } from '../nemo/frontline-config/utils'

const average = (values: number[]): number => {
  if (values.length === 0) {
    return 0
  }

  let sum = 0
  for (const value of values) {
    sum += value
  }
  return sum / values.length
}

export type CalculationAnswers = Record<AssessmentQuestionKey, number[]>

const calculateValue = (
  questions: AssessmentQuestion[],
  answers: CalculationAnswers,
  multiplierKey: 'multiplier' | 'criticality_multiplier',
  valueKey: 'value' | 'criticality_value'
): number => {
  let value = 0

  for (const question of questions) {
    const answersForQuestion = answers[question.key] || []
    const choices: number[] = []

    for (const answer of answersForQuestion) {
      const choice = question.choices.find(v => v.key === answer)
      if (choice != null) {
        choices.push(choice[valueKey])
      }
    }

    value += Math.round(average(choices)) * question[multiplierKey]
  }

  return value
}

export const getCriticality = (questions: AssessmentQuestion[], answers: CalculationAnswers): number =>
  calculateValue(questions, answers, 'criticality_multiplier', 'criticality_value')

export const getCostSensitivity = (questions: AssessmentQuestion[], answers: CalculationAnswers): number =>
  calculateValue(questions, answers, 'multiplier', 'value')

export interface CriticalityAndCostSensitivity {
  criticality: number
  costSensitivity: number
}

export const criticalitiesAndCostSensitivities = (
  criticalityQuestions: AssessmentQuestion[],
  costSensitivityQuestions: AssessmentQuestion[],
  answers: CalculationAnswers
): CriticalityAndCostSensitivity => ({
  costSensitivity: getCostSensitivity(costSensitivityQuestions, answers),
  criticality: getCriticality(criticalityQuestions, answers)
})

export const collectAnswers = (
  equipmentGroupId: EquipmentGroupStId,
  equipmentIds: EquipmentStId[],
  assessmentEquipmentAnswers: AssessmentEquipmentAnswers,
  assessmentEquipmentGroupAnswers: AssessmentEquipmentGroupAnswers
): CalculationAnswers => {
  const answers = {} as CalculationAnswers

  for (const equipmentId of equipmentIds) {
    const answersForEquipment = assessmentEquipmentAnswers[equipmentId] || {}
    for (const key in answersForEquipment) {
      const answer = answersForEquipment[key as EquipmentQuestionKey]!
      if (answers.hasOwnProperty(key)) {
        answers[key as EquipmentQuestionKey].push(answer)
      } else {
        answers[key as EquipmentQuestionKey] = [answer]
      }
    }
  }

  const answersForEquipmentGroup = assessmentEquipmentGroupAnswers[equipmentGroupId] || {}
  for (const key in answersForEquipmentGroup) {
    const answer = answersForEquipmentGroup[key as BuildingQuestionKey]!
    if (answers.hasOwnProperty(key)) {
      answers[key as BuildingQuestionKey].push(answer)
    } else {
      answers[key as BuildingQuestionKey] = [answer]
    }
  }

  return answers
}

const categoryCriticalityCostSensitivity = (
  answers: CalculationAnswers,
  questions: AssessmentQuestion[]
): CriticalityAndCostSensitivity => {
  const criticalityQuestions = questions.filter(q => q.criticality)
  const costSensitivityQuestions = questions.filter(q => q.cost_sensitive)
  return criticalitiesAndCostSensitivities(criticalityQuestions, costSensitivityQuestions, answers)
}

export interface RecommendedOffering {
  costSensitivity: CriticalityOrCostSensitivity
  criticality: CriticalityOrCostSensitivity
  scope: FlexibleScope
}

const calculateMatrixBoundaries = (answers: CalculationAnswers, questions: AssessmentQuestion[]) =>
  R.keys(answers).reduce(
    (acc, key: AssessmentQuestionKey) => {
      const question = questions.find(q => q.key === key)
      if (question) {
        const { choices, cost_sensitive, criticality, criticality_multiplier, multiplier } = question
        const criticalityValues = choices.map(choice => choice.criticality_value)
        const costSensisitiviesValues = choices.map(choice => choice.value)

        const criticalityMin =
          acc.criticalityMin + (criticality ? Math.min.apply(null, criticalityValues) * criticality_multiplier : 0)
        const criticalityMax =
          acc.criticalityMax + (criticality ? Math.max.apply(null, criticalityValues) * criticality_multiplier : 0)

        const costSensitivityMin =
          acc.costSensitivityMin + (cost_sensitive ? Math.min.apply(null, costSensisitiviesValues) * multiplier : 0)
        const costSensitivityMax =
          acc.costSensitivityMax + (cost_sensitive ? Math.max.apply(null, costSensisitiviesValues) * multiplier : 0)

        return {
          costSensitivityMax,
          costSensitivityMin,
          criticalityMax,
          criticalityMin
        }
      }
      return acc
    },
    {
      costSensitivityMax: 0,
      costSensitivityMin: 0,
      criticalityMax: 0,
      criticalityMin: 0
    }
  )

const calculateRecommendedOffering = (
  answers: CalculationAnswers,
  questions: AssessmentQuestion[],
  mccs: { criticality: number; costSensitivity: number },
  preferredOfferingMatrix: OfferingMatrixData
): RecommendedOffering => {
  const matrixBoundaries = calculateMatrixBoundaries(answers, questions)
  const { criticality, costSensitivity } = mccs
  return preferredOfferCalculation(preferredOfferingMatrix, matrixBoundaries, criticality, costSensitivity)
}

export type RatingsResult = Record<EquipmentGroupStId, Record<EquipmentCategory, RecommendedOffering>>

export function buildingRatings(frontlineConfig: ClientFrontlineConfig, offer: Offer): RatingsResult {
  const { assessmentEquipmentAnswers, assessmentEquipmentGroupAnswers } = offer
  const { assessmentQuestions, preferredOfferingMatrix } = frontlineConfig

  // XL assessment answers are stored in normal keys, so we need to translate them here
  const elevatorXLQuestions = (assessmentQuestions.group.all as AssessmentQuestion[]).concat(
    assessmentQuestions.elevator_xl.all.map(
      (q): ElevatorAssessmentQuestion => ({
        ...q,
        subject: 'elevator',
        key: translateXLAssessmentQuestionAnswerKey(q.key) as ElevatorQuestionKey
      })
    )
  )
  const escalatorXLQuestions = (assessmentQuestions.group.all as AssessmentQuestion[]).concat(
    assessmentQuestions.escalator_xl.all.map(
      (q): EscalatorAssessmentQuestion => ({
        ...q,
        subject: 'escalator',
        key: translateXLAssessmentQuestionAnswerKey(q.key) as EscalatorQuestionKey
      })
    )
  )
  const doorXLQuestions = (assessmentQuestions.group.all as AssessmentQuestion[]).concat(
    assessmentQuestions.door_xl.all.map(
      (q): DoorAssessmentQuestion => ({
        ...q,
        subject: 'door',
        key: translateXLAssessmentQuestionAnswerKey(q.key) as DoorQuestionKey
      })
    )
  )

  const resiflowXLQuestions: AssessmentQuestion[] = []

  const equipment = R.chain(building => building.details.equipment, offer.buildings)
  const equipmentByEquipmentGroup = R.groupBy(e => e.groupId, equipment)

  return R.map(equipmentGroup => {
    const equipmentGroupId = equipmentGroup.salesToolId
    const equipmentInGroup = equipmentByEquipmentGroup[equipmentGroupId] || []
    const equipmentCategories = equipmentInGroup.reduce(
      (acc, e) => acc.add(e.equipmentCategory),
      new Set<EquipmentCategory>()
    )
    const equipmentIds = equipmentInGroup.map(e => e.salesToolId)
    const answers = collectAnswers(
      equipmentGroupId,
      equipmentIds,
      assessmentEquipmentAnswers,
      assessmentEquipmentGroupAnswers
    )

    const recommendedOffers = {} as Record<EquipmentCategory, RecommendedOffering>

    for (const equipmentCategory of equipmentCategories) {
      const questions = (() => {
        switch (equipmentCategory) {
          case 'elevator':
            return elevatorXLQuestions
          case 'escalator':
            return escalatorXLQuestions
          case 'door':
            return doorXLQuestions
          case 'resiflow':
            return resiflowXLQuestions
        }
      })()
      const mccs = categoryCriticalityCostSensitivity(answers, questions)
      recommendedOffers[equipmentCategory] = calculateRecommendedOffering(
        answers,
        questions,
        mccs,
        preferredOfferingMatrix
      )
    }

    return recommendedOffers
  }, offer.equipmentGroups)
}
